import type { TtqSession } from './ttqService';

/** Sticky until mutation invalidate; TTL is a safety net only. */
const LIST_TTL_MS = 10 * 60 * 1000;
const DETAIL_TTL_MS = 10 * 60 * 1000;

type ListEntry = {
  expiresAt: number;
  sessions: TtqSession[];
  subjectParticleTotal: number | null;
};

type DetailEntry = {
  expiresAt: number;
  session: TtqSession;
};

const listCache = new Map<string, ListEntry>();
const detailCache = new Map<string, DetailEntry>();

function listKey(userId: string, subject: string) {
  return `${String(userId || '').trim()}:${String(subject || '').trim()}`;
}

function cloneSession(session: TtqSession): TtqSession {
  return {
    ...session,
    particles: Array.isArray(session.particles)
      ? session.particles.map((particle) => ({ ...particle }))
      : session.particles,
    pathRuns: Array.isArray(session.pathRuns)
      ? session.pathRuns.map((run) => (
        run && typeof run === 'object' ? { ...run } : run
      ))
      : session.pathRuns,
    ipttqRecords: Array.isArray(session.ipttqRecords)
      ? session.ipttqRecords.map((record) => (
        record && typeof record === 'object' ? { ...record } : record
      ))
      : session.ipttqRecords,
  };
}

/**
 * 列表接口常只带 status + derivedParticleIds（无 source/path/id）。
 * 若写入详情缓存，画布叠放/聚合会把全部衍生当成原始题横排。
 */
function pathRunsLayoutScore(session: TtqSession | null | undefined): number {
  const runs = session?.pathRuns;
  if (!Array.isArray(runs) || runs.length === 0) return 0;
  let score = 0;
  for (const run of runs) {
    if (!run || typeof run !== 'object') continue;
    if (String((run as { id?: string }).id || '').trim()) score += 4;
    if (String(run.sourceParticleId || '').trim()) score += 2;
    if (String(run.pathId || run.ttqAngleId || '').trim()) score += 2;
    if (Array.isArray(run.sourceParticleIds) && run.sourceParticleIds.some((id) => String(id || '').trim())) {
      score += 1;
    }
  }
  return score;
}

function isListPathRunsStub(session: TtqSession | null | undefined): boolean {
  const runs = session?.pathRuns;
  if (!Array.isArray(runs) || runs.length === 0) return false;
  return runs.some((run) => {
    if (!run || typeof run !== 'object') return false;
    const hasDerived = (run.derivedParticleIds?.length ?? 0) > 0;
    const hasSource = Boolean(String(run.sourceParticleId || '').trim())
      || (Array.isArray(run.sourceParticleIds) && run.sourceParticleIds.some((id) => String(id || '').trim()));
    const hasPath = Boolean(String(run.pathId || run.ttqAngleId || '').trim());
    const hasId = Boolean(String((run as { id?: string }).id || '').trim());
    return hasDerived && !hasSource && !hasPath && !hasId;
  });
}

export function peekTtqSessionList(userId: string, subject: string): ListEntry | null {
  const key = listKey(userId, subject);
  const entry = listCache.get(key);
  if (!entry || entry.expiresAt <= Date.now()) {
    if (entry) listCache.delete(key);
    return null;
  }
  return {
    expiresAt: entry.expiresAt,
    sessions: entry.sessions.map(cloneSession),
    subjectParticleTotal: entry.subjectParticleTotal,
  };
}

export function setTtqSessionList(
  userId: string,
  subject: string,
  sessions: TtqSession[],
  subjectParticleTotal: number | null,
) {
  listCache.set(listKey(userId, subject), {
    expiresAt: Date.now() + LIST_TTL_MS,
    sessions: sessions.map(cloneSession),
    subjectParticleTotal,
  });
  // 列表 pathRuns 常为残缺 stub，禁止写入详情缓存（否则进会话页会跳过全量拉取，叠放/聚合全坏）
}

export function invalidateTtqSessionList(userId?: string, subject?: string) {
  if (!userId) {
    listCache.clear();
    return;
  }
  if (subject) {
    listCache.delete(listKey(userId, subject));
    return;
  }
  const prefix = `${String(userId).trim()}:`;
  for (const key of [...listCache.keys()]) {
    if (key.startsWith(prefix)) listCache.delete(key);
  }
}

export function peekTtqSessionDetail(sessionId: string): TtqSession | null {
  const id = String(sessionId || '').trim();
  if (!id) return null;
  const entry = detailCache.get(id);
  if (!entry || entry.expiresAt <= Date.now()) {
    if (entry) detailCache.delete(id);
    return null;
  }
  // 残缺列表 stub：丢弃并强制走网络全量详情
  if (isListPathRunsStub(entry.session)) {
    detailCache.delete(id);
    return null;
  }
  return cloneSession(entry.session);
}

export function rememberTtqSessionDetail(session: TtqSession): TtqSession {
  if (!session?.id) return session;
  const existing = detailCache.get(session.id)?.session;
  let next = session;
  if (existing && isListPathRunsStub(session) && pathRunsLayoutScore(existing) > pathRunsLayoutScore(session)) {
    next = { ...session, pathRuns: existing.pathRuns };
  } else if (existing && pathRunsLayoutScore(existing) > pathRunsLayoutScore(session)) {
    next = { ...session, pathRuns: existing.pathRuns };
  }
  detailCache.set(session.id, {
    expiresAt: Date.now() + DETAIL_TTL_MS,
    session: cloneSession(next),
  });
  return next;
}

export function invalidateTtqSessionDetail(sessionId?: string) {
  if (!sessionId) {
    detailCache.clear();
    return;
  }
  detailCache.delete(String(sessionId).trim());
}

export function invalidateTtqCachesForSession(sessionId?: string, subject?: string) {
  if (sessionId) {
    invalidateTtqSessionDetail(sessionId);
  }
  if (subject) {
    const suffix = `:${String(subject).trim()}`;
    for (const key of [...listCache.keys()]) {
      if (key.endsWith(suffix)) listCache.delete(key);
    }
    return;
  }
  listCache.clear();
}

/** After a successful session write: keep latest detail, drop stale lists. */
export function afterTtqSessionWrite(session: TtqSession): TtqSession {
  const subject = String((session as TtqSession & { subjectCode?: string }).subjectCode || '').trim();
  if (subject) {
    const suffix = `:${subject}`;
    for (const key of [...listCache.keys()]) {
      if (key.endsWith(suffix)) listCache.delete(key);
    }
  } else {
    listCache.clear();
  }
  rememberTtqSessionDetail(session);
  return session;
}
